import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./styles.css";
import { Breadcrumb, CustomPopUpModal } from "../../Components/Atoms";
import PATHS from "../../Routes/paths";
import { getRoutes, saveRoute, updateRoute } from "../../APIs";
import { DEVICE_HWTYP_VALUES } from "../../Library/constants";
import { StoreContext } from "../../Library/contexts";
import { Button, Input } from "@chakra-ui/react";
import { ArrowRightIcon, SuccessIcon } from "../../Assets/images/svgs";
import { ReactTable, RouteMap } from "../../Components/Molecules";
import { ALERT_VEHICLE_TABLE_HEADERS } from "../../Library/tableHeaders";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { sphericalCosinus } from "../../Library/geodist";

const SetRoutes = () => {
  const [ error, setError ] = useState("");
  const [ showSuccessModal, setShowSuccessModal ] = useState(false);
  const [ routeTitle, setRouteTitle ] = useState("");
  const [ routeDst, setRouteDst ] = useState(0);
  const [ routePoints, setRoutePoints ] = useState([]);
  const [ routeToEdit, setRouteToEdit ] = useState({});
  const [ searchParams ] = useSearchParams();
  const [ isRouteEdit ] = useState(searchParams.get("isEdit"));
  const [ selectedTableRows, setSelectedTableRows ] = useState([]);

  const { userVehicles, allVehicleRoutes, setAllVehicleRoutes, lastViewState, setLastViewState } = useContext(StoreContext);
  const userVehMap = useRef({});
  const selectedVids = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const haveRoutePointsBeenEdited = useRef(false);

  const breadcrumbPaths = [
    { idx: 1, name: "View Routes", link: PATHS.VIEW_ROUTES },
    { idx: 2, name: "Set route", isCurrentPage: true },
  ];

  useEffect(() => {
    userVehicles.forEach(veh => {
      userVehMap.current[veh.idx] = veh
    });
  }, [ userVehicles ]);

  useEffect(() => {
    if (!isRouteEdit) return;

    if (!allVehicleRoutes || !allVehicleRoutes.length) {
      getRoutes((err, data) => {
        if (err) console.error(err);
        else {
          let routeIdx = searchParams.get("idx");
          let editRoute = data.find(route => route.idx === +routeIdx);
          if (editRoute && editRoute.idx) {
            setRouteDst(editRoute.totalDistance);
            setRoutePoints(editRoute.fences.map(fence => { fence.label = fence.title; return fence }));
            setSelectedTableRows(editRoute.vids);
            setRouteTitle(editRoute.nm);
            setRouteToEdit(editRoute);
          }
          setAllVehicleRoutes(data);
        }
      });
    } else {
      let routeIdx = searchParams.get("idx");
      let editRoute = allVehicleRoutes.find(route => route.idx === +routeIdx);
      if (editRoute && editRoute.idx) {
        setRouteDst(editRoute.totalDistance);
        setRoutePoints(editRoute.fences.map(fence => { fence.label = fence.title; return fence }));
        setSelectedTableRows(editRoute.vids);
        setRouteTitle(editRoute.nm);
        setRouteToEdit(editRoute);
      }
    }
  }, [ allVehicleRoutes ]);

  useEffect(() => {
    if (lastViewState.path) setLastViewState({ ...lastViewState, path: null });

    return () => {
      setLastViewState({ ...lastViewState, path: `${location.pathname}${location.search}` });
    }
  }, []);

  const handleSaveRoute = () => {
    let data = {};

    if (routeTitle) {
      data.nm = routeTitle;
    } else return setError("Please enter title for this route.");

    let newRoutePoints = [ ...routePoints ];
    let newRouteDst = 0;

    for (let i = 0; i < (newRoutePoints.length - 1); i++) {
      const p1 = newRoutePoints[i];
      const p2 = newRoutePoints[i + 1];

      if (!p2) continue;

      let pointDistance = sphericalCosinus(p1.lat, p1.lng, p2.lat, p2.lng);
      newRoutePoints[i + 1].dst = ((pointDistance / 1000) || 0);
      newRouteDst += ((pointDistance / 1000) || 0);
    }

    if (newRoutePoints && newRoutePoints.length) {
      let fences = newRoutePoints.map(point => ({
        clat: point.lat,
        clong: point.lng,
        r: 20,
        title: point.label,
        ts: Date.now(),
        typ: 2,
        dst: +((point.dst || 0).toFixed(2)),
      }));
      data.fences = fences;
      data.totalDistance = newRouteDst;
    } else return setError("Please draw a path to save this route");

    if (selectedVids.current && selectedVids.current.length) {
      data.vids = selectedVids.current;
    } else return setError("Please select vehicles.");

    saveRoute(data, (err) => {
      if (err) {
       console.error(err);
       setError(err);
     } else {
        setShowSuccessModal({
          open: true,
          modalBody: (
            <div className="link-copy-success">
              <SuccessIcon />
              <p>Route saved successfully!</p>
            </div>
          ),
          onModalClose: () => {},
        });

        setTimeout(() => {
          setShowSuccessModal({ open: false });
          navigate(PATHS.VIEW_ROUTES);
        }, 2000);
      }
    });
  }

  const tableColumns = useMemo(
    ()=> Object.values(ALERT_VEHICLE_TABLE_HEADERS).map((header)=>{
      return {
        Header: header.nm,
        accessor: header.key,
        disableSortBy: !header.sorting,
        Cell:(row) => {
          if (row.column.id === "hwtyp") {
            let devName = DEVICE_HWTYP_VALUES[row.value || 1];
            return (
              <span className="geo-fence-cell">
                {devName ? devName : "-"}
              </span>
            )
          }

          return <span className="geo-fence-cell">
            {row.value}
          </span>
        }
      }
    })
  ,[])

  const handleRowSelect = (_, selected) => {
    if (selected && selected.length) {
      selectedVids.current = selected.map(selection => selection.original.idx);
    }
  }

  const handlePointInfoWindow = (point, index) => {
    global.EE.emitEvent(global.EE.EVENT_TYPES.ROUTE_POINT_CLICKED, { ...point, vertex: index });
  }

  function hasVidArrayChanged(arr1, arr2) {
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);

    if (set1.size !== set2.size) return true;

    for (let val of set1) {
      if (!set2.has(val)) return true;
    }

    return false;
  }

  const handleEditRoute = () => {
    let dataToUpdate = { idx: +searchParams.get("idx") };
    if (routeToEdit.nm !== routeTitle) dataToUpdate.nm = routeTitle;
    if (hasVidArrayChanged(routeToEdit.vids, selectedVids.current)) dataToUpdate.vids = selectedVids.current;
    if (haveRoutePointsBeenEdited.current) {
      let newRoutePoints = [ ...routePoints ];
      let newRouteDst = 0;

      for (let i = 0; i < (newRoutePoints.length - 1); i++) {
        const p1 = newRoutePoints[i];
        const p2 = newRoutePoints[i + 1];

        if (!p2) continue;

        let pointDistance = sphericalCosinus(p1.lat, p1.lng, p2.lat, p2.lng);
        newRoutePoints[i + 1].dst = ((pointDistance / 1000) || 0);
        newRouteDst += ((pointDistance / 1000) || 0);
      }

      if (newRoutePoints && newRoutePoints.length) {
        let fences = newRoutePoints.map(point => {
          let fence = {
            clat: point.lat,
            clong: point.lng,
            r: 20,
            title: point.label,
            ts: Date.now(),
            typ: 2,
          };

          if (point.dst) fence.dst = +(point.dst.toFixed(2));
          return fence;
        });
        dataToUpdate.fences = fences;
        dataToUpdate.totalDistance = newRouteDst;
        dataToUpdate.vids = selectedVids.current;
      }
    }

    updateRoute(dataToUpdate, (err) => {
      if (err) {
       console.error(err);
       setError(err);
     } else {
        setShowSuccessModal({
          open: true,
          modalBody: (
            <div className="link-copy-success">
              <SuccessIcon />
              <p>Route updated successfully!</p>
            </div>
          ),
          onModalClose: () => {},
        });

        setTimeout(() => {
          setShowSuccessModal({ open: false });
          navigate(`${PATHS.VIEW_ROUTES}?fromUpdate=true`);
        }, 2000);
      }
    });
  }

  return (
    <section className="panel-main set-route">
      {((!isRouteEdit) || (isRouteEdit && allVehicleRoutes && allVehicleRoutes.length)) ? <><div>
        <Breadcrumb paths={breadcrumbPaths} />
      </div>
      <div className="set-route-container">
        <div className="route-map-input-box">
          {(!isRouteEdit || (isRouteEdit && routeToEdit.fences && routeToEdit.fences.length)) ? <RouteMap initialRoutePoints={isRouteEdit ? routeToEdit.fences.map(fence => { fence.lat = fence.clat; fence.lng = fence.clong; return fence; }) : []} onRoutePoint={(dst, points) => {setRouteDst(dst); if (points) setRoutePoints(points); haveRoutePointsBeenEdited.current = true; }} /> : null}
          <div className="route-input-dst-box">
            <Input value={routeTitle} onChange={(event) => {setRouteTitle(event.target.value); setError("");}} placeholder="Give this route a name" className="route-name" />
            <div className="route-dist-box">Route Distance : {(+routeDst || 0).toFixed(2)} kms</div>
          </div>
          <div className="routes-point-container">
            {routePoints && routePoints.length ? <div className="all-points-container">
              {routePoints.map((point, i) => (
                <>
                  <div className="single-point-container" onClick={() => handlePointInfoWindow(point, i)}>
                    {point.label}
                  </div>
                  {i !== (routePoints.length - 1) && <ArrowRightIcon className="point-arrow" />}
                </>
              ))}
            </div> : <p className="no-point-text">Hover and add points on the map to start creating route</p>}
          </div>
        </div>

        {(!isRouteEdit || (isRouteEdit && selectedTableRows && selectedTableRows.length)) ? <div className="set-route-select-vehicle">
          <ReactTable
            columns={tableColumns}
            showSearch={true}
            heading={"Select Vehicles"}
            data={userVehicles || []}
            pageSize={10}
            showSelection={true}
            showSelectAll={true}
            checkboxLabel={"Select All"}
            onRowSelect={handleRowSelect}
            selectedVids={selectedTableRows}
          />
          <div className="save-route-btn-box">
            <p className={`${error ? "err-msg" : "info-msg"}`}>{error || ""}</p>
            <Button onClick={isRouteEdit ? handleEditRoute : handleSaveRoute} className="save-route-btn">Save Route</Button>
          </div>
        </div> : null}
        {/* <div className={`route-sequence-box ${(geofenceSeq && geofenceSeq.length) ? "" : "centeralize-text"}`}>
          {geofenceSeq && geofenceSeq.length ? geofenceSeq.map((fence, idx) => renderGeofenceCard(fence, "is-selected", fence.idx === geofenceSeq[geofenceSeq.length - 1].idx, idx + 1)) : <p>Click on the geofences below to create a route by sequencing geofences</p>}
        </div>
        <div className="save-route-btn-box">
          <div>
            <Button onClick={() => setShowGeofences(false)} className="save-route-btn change-vehicle">
              Change Vehicles
            </Button>
            <Button onClick={handleSaveRoute} className="save-route-btn">Save Route</Button>
          </div>
        </div>

        <div className="geofence-selection-list">
          {geofences && geofences.length ? geofences.map(fence => renderGeofenceCard(fence)) : <p className="empty-geofence-list">{geofenceSeq && geofenceSeq.length ? "All geofences have been selected for this route" : "No geofence available for this vehicle"}</p>}
        </div> */}
      </div>
        {/* :
      <div className="set-route-select-vehicle">
        <ReactTable
          columns={tableColumns}
          showSearch={true}
          heading={"Select Vehicles"}
          data={userVehicles || []}
          pageSize={10}
          showSelection={true}
          showSelectAll={true}
          checkboxLabel={"Select All"}
          onRowSelect={handleRowSelect}
          selectedRows={selectedVids.current}
        />
        <div className="save-route-btn-box">
          <p className={`${error ? "err-msg" : ""}`}>{error || ""}</p>
          <Button onClick={handleNext} className="save-route-btn next-btn">Next</Button>
        </div>
      </div>} */}

      <CustomPopUpModal { ...showSuccessModal } /></> : null}
    </section>
  )
}

export default SetRoutes
